import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    Chip,
    Divider,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Switch,
    FormControlLabel,
    Autocomplete,
    CircularProgress,
    Snackbar,
    Alert,
    Skeleton,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Check as CheckIcon,
    AccessTime as PendingIcon,
    BeachAccess as VacationIcon,
    Sick as SickIcon,
    CalendarMonth as HolidayIcon,
    EventBusy as BereavementIcon,
    Gavel as JuryIcon,
    Today as TodayIcon,
    QrCodeScanner as ScanIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, addDays, isSameWeek, startOfWeek } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import TimesheetService from "../services/Timesheet.service";
import QuoteService from "../services/Quote.service";
import WeekPicker from "../components/pickers/WeekPicker";
import WorkOrderTravelerPicker from "../components/pickers/WorkOrderTravelerPicker";

// ── ISO week helpers ──────────────────────────────────────────────────────────

function toISOWeekString(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}W${String(week).padStart(2, "0")}`;
}


function formatDate(dateStr) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}

function formatTime(timeStr) {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(h, 10), parseInt(m, 10));
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function todayString() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

const hasText = (value) => Boolean(value && String(value).trim());

// Snaps a "HH:mm" string to the nearest 15-minute mark (:00, :15, :30, :45).
function roundToQuarterHour(value) {
    if (!value) return "00:00";
    const [h, m] = String(value).slice(0, 5).split(":").map(Number);
    let totalMinutes = Math.round((h * 60 + m) / 15) * 15;
    totalMinutes = Math.max(0, Math.min(totalMinutes, 23 * 60 + 45));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

// TimePicker works in Date objects; the form stores plain "HH:mm" strings.
function timeStringToDate(value) {
    const [h, m] = String(value || "00:00").slice(0, 5).split(":").map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
}

// Mirrors the backend's duration calc: hours between start/end, minus 30 min for lunch.
function computeDuration(startTime, endTime, lunch) {
    if (!startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    const minutes = (eh * 60 + em) - (sh * 60 + sm);
    if (minutes <= 0) return 0;
    const hours = minutes / 60 - (lunch ? 0.5 : 0);
    return Math.max(0, hours);
}

// ── PTO icon helper ───────────────────────────────────────────────────────────

const PTO_META = {
    Vacation:    { icon: <VacationIcon />,    color: "secondary" },
    Sick:        { icon: <SickIcon />,        color: "warning" },
    Holiday:     { icon: <HolidayIcon />,     color: "success" },
    Bereavement: { icon: <BereavementIcon />, color: "info" },
    "Jury Duty": { icon: <JuryIcon />,        color: "default" },
};

const PTO_TYPES = ["Vacation", "Sick", "Holiday", "Bereavement", "Jury Duty"];

// ── Blank form states ─────────────────────────────────────────────────────────

const blankSegment = {
    date: todayString(),
    start_time: "07:00",
    end_time: "15:30",
    lunch: true,
    segment_type_id: "",
    linkMode: "project", // "project" | "quote" | "traveler"
    project: null,
    quote: null,
    work_order_traveler: null,
    is_project_labor: false,
    travel_duration: 0,
    notes: "",
};

const blankPTO = {
    date: todayString(),
    PTO_type: "Vacation",
    is_full_day: true,
    is_paid: true,
    notes: "",
};

// ── Sub-components ────────────────────────────────────────────────────────────

function SegmentCard({ segment, onEdit, onDelete }) {
    const approved = segment.is_approved;
    return (
        <Card
            sx={{
                mb: 1.5,
                boxShadow: "none",
                border: 1,
                borderColor: approved ? "success.main" : "primary.main",
                borderRadius: 3,
            }}
        >
            <CardContent sx={{ pb: "12px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {formatDate(segment.date)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatTime(segment.start_time)} – {formatTime(segment.end_time)}
                            {segment.lunch ? "  ·  Lunch" : ""}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {capitalize(segment.segment_type?.name ?? "")}
                        </Typography>
                        {segment.project_number && (
                            <Typography variant="body2" color="text.secondary">
                                {segment.project_number}
                                {segment.project_name ? ` – ${segment.project_name}` : ""}
                            </Typography>
                        )}
                        {segment.work_order_number && (
                            <Typography variant="body2" color="text.secondary">
                                {segment.work_order_number}
                                {segment.traveler_step_name
                                    ? ` – ${segment.traveler_step_name}`
                                    : ""}
                            </Typography>
                        )}
                        {Number(segment.travel_duration) > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                Travel: {segment.travel_duration} hr
                            </Typography>
                        )}
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.5}>
                        <Chip
                            size="small"
                            icon={approved ? <CheckIcon /> : <PendingIcon />}
                            label={approved ? "Approved" : "Pending"}
                            color={approved ? "success" : "primary"}
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {segment.duration} hr{segment.duration !== 1 ? "s" : ""}
                        </Typography>
                        {!approved && (
                            <Stack direction="row" spacing={0}>
                                <IconButton size="small" onClick={() => onEdit(segment)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => onDelete(segment)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
                {segment.notes ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
                        {segment.notes}
                    </Typography>
                ) : null}
            </CardContent>
        </Card>
    );
}

function PTOCard({ pto, onDelete }) {
    const meta = PTO_META[pto.PTO_type] ?? PTO_META["Vacation"];
    const approved = pto.is_approved;
    return (
        <Card
            sx={{
                mb: 1.5,
                boxShadow: "none",
                border: 1,
                borderColor: approved ? "success.main" : `${meta.color}.main`,
                borderRadius: 3,
            }}
        >
            <CardContent sx={{ pb: "12px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box color={`${meta.color}.main`}>{meta.icon}</Box>
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                            >
                                {formatDate(pto.date)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {pto.PTO_type}
                                {!pto.is_paid ? "  ·  Unpaid" : ""}
                                {!pto.is_full_day ? "  ·  Half Day" : ""}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack alignItems="flex-end" spacing={0.5}>
                        <Chip
                            size="small"
                            icon={approved ? <CheckIcon /> : <PendingIcon />}
                            label={approved ? "Approved" : "Pending"}
                            color={approved ? "success" : meta.color}
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {pto.duration} hr{pto.duration !== 1 ? "s" : ""}
                        </Typography>
                        {!approved && (
                            <IconButton size="small" color="error" onClick={() => onDelete(pto)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function SummaryCard({ summary }) {
    const rows = [
        { label: "Regular",     value: summary.regularHours },
        { label: "Overtime",    value: summary.overtimeHours },
        { label: "Double Time", value: summary.doubleTimeHours },
        { label: "Travel",      value: summary.travelHours },
        { label: "Vacation",    value: summary.approvedVacationHours },
        { label: "Pending PTO", value: summary.pendingVacationHours },
    ].filter((r) => r.value > 0);

    return (
        <Card
            sx={{
                mb: 2,
                boxShadow: "none",
                border: 1,
                borderColor: "primary.main",
                borderRadius: 3,
            }}
        >
            <CardContent>
                <Typography
                    variant="subtitle2"
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, mb: 1 }}
                >
                    Week Summary
                </Typography>
                {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No hours recorded yet.
                    </Typography>
                ) : (
                    <Stack spacing={0.25}>
                        {rows.map((r) => (
                            <Stack key={r.label} direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                    {r.label}
                                </Typography>
                                <Typography variant="body2">{r.value.toFixed(2)} hrs</Typography>
                            </Stack>
                        ))}
                        <Divider sx={{ my: 0.5 }} />
                        <Stack direction="row" justifyContent="space-between">
                            <Typography
                                variant="body2"
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                            >
                                Total
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                            >
                                {summary.totalHours.toFixed(2)} hrs
                            </Typography>
                        </Stack>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}

// ── Add / Edit segment dialog ─────────────────────────────────────────────────

function SegmentDialog({ open, onClose, onSave, editing, segment, workTypes }) {
    const [form, setForm] = useState(blankSegment);
    const [projectSearch, setProjectSearch] = useState("");
    const [projectOptions, setProjectOptions] = useState([]);
    const [projectLoading, setProjectLoading] = useState(false);
    const [quoteSearch, setQuoteSearch] = useState("");
    const [quoteOptions, setQuoteOptions] = useState([]);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            if (editing && segment) {
                setForm({
                    date: segment.date,
                    start_time: roundToQuarterHour(segment.start_time || "07:00"),
                    end_time: roundToQuarterHour(segment.end_time || "15:30"),
                    lunch: segment.lunch,
                    segment_type_id: segment.segment_type?.id ?? "",
                    linkMode: segment.work_order_traveler
                        ? "traveler"
                        : segment.quote
                          ? "quote"
                          : "project",
                    project: segment.project
                        ? { id: segment.project, label: `${segment.project_number} – ${segment.project_name}` }
                        : null,
                    quote: segment.quote
                        ? { id: segment.quote, label: `${segment.quote_number} – ${segment.quote_name}` }
                        : null,
                    work_order_traveler: segment.work_order_traveler
                        ? {
                              id: segment.work_order_traveler,
                              traveler_number: segment.traveler_number || "",
                              step_name: segment.traveler_step_name || "",
                              status: segment.traveler_status || "",
                              work_order: {
                                  wo_number: segment.work_order_number || "",
                                  project_detail: segment.project
                                      ? {
                                            id: segment.project,
                                            number: segment.project_number || "",
                                            name: segment.project_name || "",
                                        }
                                      : null,
                              },
                          }
                        : null,
                    is_project_labor: segment.is_project_labor,
                    travel_duration: segment.travel_duration ?? 0,
                    notes: segment.notes ?? "",
                });
                setProjectSearch("");
                setProjectOptions([]);
                setQuoteSearch("");
                setQuoteOptions([]);
            } else {
                setForm(blankSegment);
                setProjectSearch("");
                setProjectOptions([]);
                setQuoteSearch("");
                setQuoteOptions([]);
            }
        }
    }, [open, editing, segment]);

    useEffect(() => {
        if (!form.is_project_labor || form.linkMode !== "project") return;
        if (projectSearch.length < 2) {
            setProjectOptions([]);
            return;
        }
        setProjectLoading(true);
        const timer = setTimeout(() => {
            TimesheetService.searchProjects(projectSearch)
                .then((res) => {
                    setProjectOptions(
                        (res.data.results ?? res.data).map((p) => ({
                            id: p.id,
                            label: `${p.number} – ${p.name}`,
                        }))
                    );
                })
                .catch(() => {})
                .finally(() => setProjectLoading(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [projectSearch, form.is_project_labor, form.linkMode]);

    useEffect(() => {
        if (!form.is_project_labor || form.linkMode !== "quote") return;
        if (quoteSearch.length < 2) {
            setQuoteOptions([]);
            return;
        }
        setQuoteLoading(true);
        const timer = setTimeout(() => {
            QuoteService.search(quoteSearch)
                .then((res) => {
                    const results = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
                    setQuoteOptions(
                        results.map((q) => ({
                            id: q.id,
                            label: `${q.number} – ${q.name}`,
                        }))
                    );
                })
                .catch(() => {})
                .finally(() => setQuoteLoading(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [quoteSearch, form.is_project_labor, form.linkMode]);

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
    const toggle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

    const selectedWorkType = workTypes.find((t) => t.id === form.segment_type_id);
    const isFieldWorkType =
        String(selectedWorkType?.name || "").trim().toLowerCase() === "field";
    const isShopWorkType =
        String(selectedWorkType?.name || "").trim().toLowerCase() === "shop";

    const handleSave = async () => {
        setSaving(true);
        const usingTraveler = form.is_project_labor && form.linkMode === "traveler";
        const payload = {
            date: form.date,
            start_time: form.start_time,
            end_time: form.end_time,
            lunch: form.lunch,
            segment_type_id: form.segment_type_id,
            project:
                form.is_project_labor && form.linkMode === "project" && form.project
                    ? form.project.id
                    : null,
            quote:
                form.is_project_labor && form.linkMode === "quote" && form.quote
                    ? form.quote.id
                    : null,
            work_order_traveler:
                usingTraveler && form.work_order_traveler
                    ? form.work_order_traveler.id
                    : null,
            is_project_labor: usingTraveler
                ? Boolean(form.work_order_traveler?.work_order?.project_detail)
                : form.is_project_labor && (!!form.project || !!form.quote),
            travel_duration: isFieldWorkType ? form.travel_duration || 0 : 0,
            notes: form.notes,
        };
        try {
            await onSave(payload, editing ? segment.id : null);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const hasLink =
        form.is_project_labor &&
        (form.linkMode === "traveler"
            ? Boolean(form.work_order_traveler)
            : form.linkMode === "quote"
              ? Boolean(form.quote)
              : Boolean(form.project));
    const notesMissing = !hasLink && !hasText(form.notes);
    const liveDuration = computeDuration(form.start_time, form.end_time, form.lunch);

    const valid =
        form.date &&
        form.start_time &&
        form.end_time &&
        form.segment_type_id &&
        form.start_time < form.end_time &&
        !notesMissing;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                {editing ? "Edit Time Entry" : "Add Time Entry"}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Date"
                        type="date"
                        value={form.date}
                        onChange={set("date")}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Stack direction="row" spacing={2}>
                            <TimePicker
                                label="Start Time"
                                value={timeStringToDate(form.start_time)}
                                onChange={(newVal) =>
                                    newVal &&
                                    setForm((f) => ({
                                        ...f,
                                        start_time: roundToQuarterHour(format(newVal, "HH:mm")),
                                    }))
                                }
                                minutesStep={15}
                                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            />
                            <TimePicker
                                label="End Time"
                                value={timeStringToDate(form.end_time)}
                                onChange={(newVal) =>
                                    newVal &&
                                    setForm((f) => ({
                                        ...f,
                                        end_time: roundToQuarterHour(format(newVal, "HH:mm")),
                                    }))
                                }
                                minutesStep={15}
                                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            />
                        </Stack>
                    </LocalizationProvider>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                            control={<Switch checked={form.lunch} onChange={toggle("lunch")} />}
                            label="Lunch (30 min deducted)"
                        />
                        <Typography
                            variant="body2"
                            color={liveDuration > 0 ? "text.secondary" : "error"}
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {liveDuration > 0
                                ? `${liveDuration.toFixed(2)} hr${liveDuration !== 1 ? "s" : ""}`
                                : "Invalid range"}
                        </Typography>
                    </Stack>
                    <TextField
                        label="Work Type"
                        select
                        value={form.segment_type_id}
                        onChange={(e) => {
                            const newId = e.target.value;
                            const newType = workTypes.find((t) => t.id === newId);
                            const newIsShop =
                                String(newType?.name || "").trim().toLowerCase() === "shop";
                            setForm((f) => ({
                                ...f,
                                segment_type_id: newId,
                                ...(!newIsShop && f.linkMode === "traveler"
                                    ? {
                                          linkMode: "project",
                                          work_order_traveler: null,
                                      }
                                    : {}),
                            }));
                        }}
                        fullWidth
                        size="small"
                    >
                        {workTypes.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {capitalize(t.name)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.is_project_labor}
                                onChange={toggle("is_project_labor")}
                            />
                        }
                        label="Link to project, quote, or work order"
                    />
                    {!hasLink && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                            Nothing linked — add a note below so we know what the hours were for.
                        </Typography>
                    )}
                    {form.is_project_labor && (
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            size="small"
                            value={form.linkMode}
                            onChange={(_, val) => {
                                if (val)
                                    setForm((f) => ({
                                        ...f,
                                        linkMode: val,
                                        project: null,
                                        quote: null,
                                        work_order_traveler: null,
                                    }));
                            }}
                        >
                            <ToggleButton value="project">Project</ToggleButton>
                            <ToggleButton value="quote">Quote</ToggleButton>
                            {isShopWorkType && (
                                <ToggleButton value="traveler">Work Order</ToggleButton>
                            )}
                        </ToggleButtonGroup>
                    )}
                    {form.is_project_labor && form.linkMode === "project" && (
                        <Autocomplete
                            options={projectOptions}
                            value={form.project}
                            onChange={(_, v) => setForm((f) => ({ ...f, project: v }))}
                            inputValue={projectSearch}
                            onInputChange={(_, v) => setProjectSearch(v)}
                            loading={projectLoading}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Project"
                                    size="small"
                                    placeholder="Type to search…"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {projectLoading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    )}
                    {form.is_project_labor && form.linkMode === "quote" && (
                        <Autocomplete
                            options={quoteOptions}
                            value={form.quote}
                            onChange={(_, v) => setForm((f) => ({ ...f, quote: v }))}
                            inputValue={quoteSearch}
                            onInputChange={(_, v) => setQuoteSearch(v)}
                            loading={quoteLoading}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Quote"
                                    size="small"
                                    placeholder="Type to search…"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {quoteLoading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    )}
                    {form.is_project_labor && form.linkMode === "traveler" && (
                        <>
                            <WorkOrderTravelerPicker
                                value={form.work_order_traveler}
                                onChange={(traveler) =>
                                    setForm((f) => ({ ...f, work_order_traveler: traveler }))
                                }
                            />
                            {form.work_order_traveler && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                                    {form.work_order_traveler.work_order?.project_detail
                                        ? `Billed to Project: ${form.work_order_traveler.work_order.project_detail.number} — ${form.work_order_traveler.work_order.project_detail.name}`
                                        : "Stock work order — no project. Hours are recorded against the work order only."}
                                </Typography>
                            )}
                        </>
                    )}
                    {isFieldWorkType && (
                        <TextField
                            label="Travel (hrs)"
                            type="number"
                            inputProps={{ min: 0, step: 0.25 }}
                            value={form.travel_duration}
                            onChange={set("travel_duration")}
                            fullWidth
                            size="small"
                        />
                    )}
                    <TextField
                        label="Notes"
                        multiline
                        rows={2}
                        required={!hasLink}
                        error={notesMissing}
                        value={form.notes}
                        onChange={set("notes")}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 250 }}
                        helperText={
                            notesMissing
                                ? "Required when nothing is linked."
                                : `${form.notes.length} / 250`
                        }
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!valid || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    {editing ? "Save" : "Add"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Add PTO dialog ────────────────────────────────────────────────────────────

function PTODialog({ open, onClose, onSave }) {
    const [form, setForm] = useState(blankPTO);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm(blankPTO);
    }, [open]);

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
    const toggle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave({ ...form });
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Add PTO
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Date"
                        type="date"
                        value={form.date}
                        onChange={set("date")}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="PTO Type"
                        select
                        value={form.PTO_type}
                        onChange={set("PTO_type")}
                        fullWidth
                        size="small"
                    >
                        {PTO_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={<Switch checked={form.is_full_day} onChange={toggle("is_full_day")} />}
                        label="Full day (8 hrs)"
                    />
                    <FormControlLabel
                        control={<Switch checked={form.is_paid} onChange={toggle("is_paid")} />}
                        label="Paid"
                    />
                    <TextField
                        label="Notes"
                        multiline
                        rows={2}
                        value={form.notes}
                        onChange={set("notes")}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 250 }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    Add PTO
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Delete confirm dialog ─────────────────────────────────────────────────────

function DeleteDialog({ open, onClose, onConfirm, label }) {
    const [deleting, setDeleting] = useState(false);
    const handleConfirm = async () => {
        setDeleting(true);
        try {
            await onConfirm();
            onClose();
        } finally {
            setDeleting(false);
        }
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Delete Entry
            </DialogTitle>
            <DialogContent>
                <Typography>Are you sure you want to delete this {label}?</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={deleting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    disabled={deleting}
                    startIcon={deleting ? <CircularProgress size={16} /> : null}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function Timesheets() {
    const { user } = useAuth();

    const navigate = useNavigate();
    const [isoWeek, setIsoWeek] = useState(() => toISOWeekString(new Date()));
    const [segments, setSegments] = useState([]);
    const [ptoList, setPtoList] = useState([]);
    const [summary, setSummary] = useState(null);
    const [workTypes, setWorkTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // dialog state
    const [segmentDialog, setSegmentDialog] = useState({ open: false, editing: false, segment: null });
    const [ptoDialog, setPtoDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });

    // snackbar
    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
    const toast = (message, severity = "success") => setSnack({ open: true, message, severity });

    // fetch work types once
    useEffect(() => {
        TimesheetService.getWorkTypes()
            .then((res) => setWorkTypes(res.data))
            .catch(() => {});
    }, []);

    // fetch week data whenever isoWeek changes
    const fetchWeek = useCallback(() => {
        setLoading(true);
        Promise.all([
            TimesheetService.getSegments(isoWeek),
            TimesheetService.getPTO(isoWeek),
            TimesheetService.getWeeklySummary(isoWeek),
        ])
            .then(([segsRes, ptoRes, sumRes]) => {
                const allSegs = segsRes.data.results ?? segsRes.data;
                const userSegs = allSegs.filter((s) => s.user === user?.id);
                setSegments(userSegs.sort((a, b) => b.date.localeCompare(a.date) || a.start_time.localeCompare(b.start_time)));

                const allPTO = ptoRes.data.results ?? ptoRes.data;
                const userPTO = allPTO.filter((p) => p.user === user?.id);
                setPtoList(userPTO.sort((a, b) => b.date.localeCompare(a.date)));

                setSummary(sumRes.data);
            })
            .catch(() => toast("Failed to load timesheet data.", "error"))
            .finally(() => setLoading(false));
    }, [isoWeek, user?.id]);

    useEffect(() => {
        fetchWeek();
    }, [fetchWeek]);

    // segment CRUD
    const handleSaveSegment = async (payload, id) => {
        if (id) {
            await TimesheetService.updateSegment(id, { ...payload, user: user.id });
            toast("Time entry updated.");
        } else {
            await TimesheetService.createSegment({ ...payload, user: user.id });
            toast("Time entry submitted for approval.");
        }
        fetchWeek();
    };

    const handleDeleteSegment = async () => {
        await TimesheetService.deleteSegment(deleteDialog.item.id);
        toast("Time entry deleted.", "warning");
        fetchWeek();
    };

    // PTO CRUD
    const handleSavePTO = async (payload) => {
        await TimesheetService.createPTO({ ...payload, user: user.id });
        toast("PTO submitted for approval.");
        fetchWeek();
    };

    const handleDeletePTO = async () => {
        await TimesheetService.deletePTO(deleteDialog.item.id);
        toast("PTO entry deleted.", "warning");
        fetchWeek();
    };

    const sortedAll = [
        ...segments.map((s) => ({ ...s, _type: "work" })),
        ...ptoList.map((p) => ({ ...p, _type: "pto" })),
    ].sort((a, b) => b.date.localeCompare(a.date));

    return (
        <Box sx={{ maxWidth: 600, mx: "auto", pb: 10 }}>
            {/* ── Week navigation ── */}
            <Box sx={{ mb: 2, textAlign: "center" }}>
                {/* Date range + today button */}
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                    >
                        {(() => {
                            const year = parseInt(isoWeek.slice(0, 4), 10);
                            const week = parseInt(isoWeek.slice(5), 10);
                            const jan4 = new Date(Date.UTC(year, 0, 4));
                            const dow = jan4.getUTCDay() || 7;
                            const monday = new Date(jan4);
                            monday.setUTCDate(jan4.getUTCDate() - dow + 1 + (week - 1) * 7);
                            const mon = new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate());
                            return `${format(mon, "MMM d")} – ${format(addDays(mon, 6), "MMM d, yyyy")}`;
                        })()}
                    </Typography>
                    {!isSameWeek(
                        startOfWeek(new Date(), { weekStartsOn: 1 }),
                        startOfWeek((() => {
                            const year = parseInt(isoWeek.slice(0, 4), 10);
                            const week = parseInt(isoWeek.slice(5), 10);
                            const jan4 = new Date(Date.UTC(year, 0, 4));
                            const dow = jan4.getUTCDay() || 7;
                            const monday = new Date(jan4);
                            monday.setUTCDate(jan4.getUTCDate() - dow + 1 + (week - 1) * 7);
                            return new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate());
                        })(), { weekStartsOn: 1 }),
                        { weekStartsOn: 1 }
                    ) && (
                        <Tooltip title="Go to current week">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setIsoWeek(toISOWeekString(new Date()))}
                            >
                                <TodayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                {/* Picker */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <WeekPicker isoWeek={isoWeek} getIsoWeek={setIsoWeek} hideTodayButton />
                </Box>
            </Box>

            {/* ── Summary card ── */}
            {loading ? (
                <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: 3 }} />
            ) : summary ? (
                <SummaryCard summary={summary} />
            ) : null}

            {/* ── Action buttons ── */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    endIcon={<AddIcon />}
                    onClick={() => setPtoDialog(true)}
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                >
                    PTO
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    endIcon={<AddIcon />}
                    onClick={() => setSegmentDialog({ open: true, editing: false, segment: null })}
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                >
                    Add Time
                </Button>
            </Stack>
            {/* Scanning a traveler's "Log my hours" QR opens a pre-filled entry for that WO step. */}
            <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<ScanIcon />}
                onClick={() => navigate("/scan?mode=time")}
                sx={{ mb: 2, fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
            >
                Scan Work Order QR
            </Button>

            <Divider sx={{ mb: 2 }} />

            {/* ── Entries list ── */}
            {loading ? (
                [1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={90} sx={{ mb: 1.5, borderRadius: 3 }} />
                ))
            ) : sortedAll.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
                    No entries for this week.
                </Typography>
            ) : (
                sortedAll.map((item) =>
                    item._type === "work" ? (
                        <SegmentCard
                            key={`seg-${item.id}`}
                            segment={item}
                            onEdit={(s) => setSegmentDialog({ open: true, editing: true, segment: s })}
                            onDelete={(s) => setDeleteDialog({ open: true, type: "work", item: s })}
                        />
                    ) : (
                        <PTOCard
                            key={`pto-${item.id}`}
                            pto={item}
                            onDelete={(p) => setDeleteDialog({ open: true, type: "pto", item: p })}
                        />
                    )
                )
            )}

            {/* ── Dialogs ── */}
            <SegmentDialog
                open={segmentDialog.open}
                onClose={() => setSegmentDialog((d) => ({ ...d, open: false }))}
                onSave={handleSaveSegment}
                editing={segmentDialog.editing}
                segment={segmentDialog.segment}
                workTypes={workTypes}
            />
            <PTODialog
                open={ptoDialog}
                onClose={() => setPtoDialog(false)}
                onSave={handleSavePTO}
            />
            <DeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog((d) => ({ ...d, open: false }))}
                onConfirm={deleteDialog.type === "work" ? handleDeleteSegment : handleDeletePTO}
                label={deleteDialog.type === "work" ? "time entry" : "PTO entry"}
            />

            {/* ── Snackbar ── */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snack.severity}
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    sx={{ width: "100%" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>

        </Box>
    );
}

export default Timesheets;
